import {z} from 'zod';
import {
  BlockingWidgetDisplayMode,
  CopilotWidgetType,
  EntityTypeToRename,
} from 'common/src/modules/shared/copilot/copilot.enums';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import {entityToRenameSchema} from 'common/src/modules/shared/copilot/copilot.schemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';

type BatchRenameEntitiesToolConfig = {
  entityType: EntityTypeToRename;
  toolName: string;
  displayName?: string;
};

function batchRenameEntitiesTool({
  entityType,
  toolName,
  displayName,
}: BatchRenameEntitiesToolConfig) {
  const inputSchema = z.object({
    entities: z
      .array(entityToRenameSchema)
      .describe(`List of ${entityType.toLowerCase()}s to rename`),
  });

  async function execute({
    input,
    toolUtils: {requestUserFeedback},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const result = await requestUserFeedback({
        displayMode: BlockingWidgetDisplayMode.Inline,
        widget: {
          type: CopilotWidgetType.BatchRenameWidget,
          content: {
            entityType,
            entitiesToRename: input.entities,
          },
        },
      });

      return toolResult(result);
    } catch (error) {
      return toolResult(
        `ERROR: ${error instanceof Error ? error.message : `Failed to rename ${entityType.toLowerCase()}s`}`,
      );
    }
  }

  const entityTypeLower = entityType.toLowerCase();

  return tool({
    name: toolName,
    displayName: displayName ?? `Batch renaming ${entityTypeLower}s`,
    description: `
      Shows a blocking widget to batch rename the selected ${entityTypeLower}s.
      
      ## When to Call
      Call this tool when the user wants to batch rename ${entityTypeLower}s and ${entityTypeLower}s are selected.
      
      ## Important
      - The entities array should contain objects with id, currentName, and newName (initially null).
      - The tool will show a widget where the user can edit ${entityType.toLowerCase()} names.
      - The tool waits for the user to execute the rename or cancel.
      - Returns a success message with the count of renamed ${entityTypeLower}s.
    `.trim(),
    execute,
    inputSchema,
  });
}

export default batchRenameEntitiesTool;
